import { useRef, useState } from 'react';

interface Props {
  webmSrc: string;
  mp4Src: string;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Autoplay-muted-loop video with a minimal hover/tap pause affordance.
 *
 * No native `controls` chrome — just an overlay icon (pause on hover, play
 * when paused) so users can stop the loop to study a frame. Click anywhere
 * on the video to toggle. The component fills its container (`100%` × `100%`,
 * `object-fit: cover`); pass the size and shape via the parent (phone frame,
 * card, hero slot, whatever).
 */
export const HoverPauseVideo: React.FC<Props> = ({ webmSrc, mp4Src, style, className }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      void v.play();
      setIsPaused(false);
    } else {
      v.pause();
      setIsPaused(true);
    }
  };

  const showIcon = isHovering || isPaused;

  return (
    <div
      className={className}
      style={{ position: 'relative', cursor: 'pointer', width: '100%', height: '100%', ...style }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={toggle}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      >
        <source src={webmSrc} type="video/webm" />
        <source src={mp4Src} type="video/mp4" />
      </video>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          opacity: showIcon ? 1 : 0,
          transition: 'opacity 0.15s ease',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.55)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isPaused ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zm8 0h4v14h-4z" /></svg>
          )}
        </div>
      </div>
    </div>
  );
};
